import type { TaskDoc } from "@kwp/contracts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { errorMessage, useTRPC } from "../../lib/api";
import { usePresence } from "../../lib/use-presence";
import { useToast } from "../../ui/toast";

/** A fresh idempotency key for `actions.invoke` (the api replays a retried request id). */
export function newRequestId(): string {
  return `web-${crypto.randomUUID()}`;
}

export function useMembers(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.workspaces.members.queryOptions({ workspaceId }));
}

/** Display name for a uid among the workspace members (falls back to the email, then the uid). */
export function memberLabel(
  members: ReadonlyArray<{ uid: string; name: string | null; email: string | null }> | undefined,
  uid: string,
): string {
  const m = members?.find((x) => x.uid === uid);
  if (m === undefined) return uid;
  if (m.name !== null) return m.name;
  return m.email === null ? uid : m.email;
}

/**
 * Every task change goes through the api; the Firestore listeners then show the result, so none
 * of these touch local state. Failures surface as an error toast.
 */
export function useTaskActions() {
  const trpc = useTRPC();
  const toast = useToast();
  const { ensurePresence } = usePresence();
  const onError = (error: unknown) => toast({ tone: "error", message: errorMessage(error) });

  const setStatus = useMutation(trpc.tasks.setStatus.mutationOptions({ onError }));
  const remove = useMutation(
    trpc.actions.invoke.mutationOptions({
      onError,
      onSuccess: (outcome) => {
        if (outcome.status === "done") toast({ message: "Task deleted." });
        else
          toast({
            message: "Deleting a task needs your approval.",
            action: { label: "Review in Approvals", href: "/approvals" },
          });
      },
    }),
  );

  return {
    toggle(workspaceId: string, taskId: string, done: boolean) {
      setStatus.mutate({ workspaceId, taskId, status: done ? "done" : "open" });
    },
    setStatus(workspaceId: string, taskId: string, status: TaskDoc["status"]) {
      setStatus.mutate({ workspaceId, taskId, status });
    },
    /**
     * Deleting is destructive: with a passkey, Touch ID first and it happens now; without one,
     * the api parks it as an approval (the toast links there).
     */
    async remove(workspaceId: string, taskId: string, onSettled?: () => void) {
      try {
        if ((await ensurePresence()) === "cancelled") {
          toast({ message: "Not deleted: the passkey check was cancelled." });
          onSettled?.();
          return;
        }
      } catch (error) {
        onError(error);
        onSettled?.();
        return;
      }
      remove.mutate(
        { name: "tasks.delete", input: { workspaceId, taskId }, requestId: newRequestId() },
        onSettled === undefined ? {} : { onSettled },
      );
    },
    removing: remove.isPending,
  };
}
